import { KnownError } from "@/api/client";
import {
  type MessageKind,
  type SupportFollowupItem,
  type SupportMessage,
  useAdminSupportFollowupMutation,
  useAdminSupportFollowups,
  useAdminSupportTicketStatusMutation,
} from "@/api/support";
import { cn } from "@/lib/utils";
import { DeliveryStatusChip, TicketStatusChip } from "@/routes/admin/support/components/support-status-chip";
import {
  FOLLOWUP_FILTER_OPTIONS,
  type FollowupFilter,
  canCloseFollowup,
  computeFollowupProgress,
  filterFollowups,
  formatFollowupProgress,
  groupFollowups,
  isFollowupDone,
  splitQuotedBlocks,
} from "@/routes/admin/support/lib/followups";
import { formatTicketDateTime } from "@/routes/admin/support/lib/format-datetime";
import { deliveryStatusDisplay, ticketStatusDisplay } from "@/routes/admin/support/lib/status-display";
import { AttachmentRow, MessageComposer } from "@/routes/admin/support/support-ticket-detail-page";
import { Button, Loading, Typography, useToastMessage } from "@backpac/monica";
import { useState } from "react";
import { Link } from "react-router";

/**
 * /admin/support/followups — worklist for the unanswered legacy tickets. Staff go through each
 * ticket: take the internal action and tick "조치 완료", reply to the customer ("회신 완료" is
 * derived from an actually-sent reply, never set by hand), then close the ticket.
 *
 * Colors: blue = done, orange = still to do, purple = closed. Never red/green (the operator is
 * red-green colorblind), and every state also carries a text label.
 */
export function SupportFollowupsPage() {
  const [filter, setFilter] = useState<FollowupFilter>("all");
  const { data, isLoading, isError, error, refetch } = useAdminSupportFollowups();

  const items = data?.items ?? [];
  const progress = computeFollowupProgress(items);
  const sections = groupFollowups(filterFollowups(items, filter));

  return (
    <div className="flex flex-col gap-6 max-w-[960px] mx-auto">
      <div className="flex flex-col gap-3">
        <Link to="/admin/support" className="inline-flex items-center gap-1 w-fit">
          <i className="i-bp-arrow-left size-3.5 inline-block" />
          <Typography size="xs" color="textMuted">
            문의함으로
          </Typography>
        </Link>
        <Typography asChild size="xl" font="unbounded" color="textPrimary">
          <h1>대응 목록</h1>
        </Typography>
      </div>

      {isLoading ? <Loading className="py-16" /> : null}

      {isError ? (
        <div className="rounded-[16px] bg-colorOrangeLight p-6 flex items-center justify-between gap-4 flex-wrap">
          <Typography size="sm" className="text-colorOrange">
            {KnownError.is(error) && (error.status === 401 || error.status === 403)
              ? "이 계정은 고객 문의를 볼 권한이 없습니다."
              : KnownError.is(error)
                ? error.message
                : "대응 목록을 불러오지 못했습니다."}
          </Typography>
          <Button variant="outlined" size="sm" onClick={() => refetch()}>
            다시 시도
          </Button>
        </div>
      ) : null}

      {!isLoading && !isError && data != null ? (
        <>
          <div className="sticky top-0 z-10 bg-white rounded-[16px] border border-borderLight p-4 flex items-center justify-between gap-4 flex-wrap">
            <Typography size="sm" weight={600} color="textPrimary" data-testid="followup-progress">
              {formatFollowupProgress(progress)}
            </Typography>
            <fieldset className="flex items-center gap-2 border-0 p-0 m-0" aria-label="필터">
              {FOLLOWUP_FILTER_OPTIONS.map((option) => (
                <Button
                  key={option.value}
                  size="sm"
                  variant={filter === option.value ? "black" : "outlined"}
                  aria-pressed={filter === option.value}
                  onClick={() => setFilter(option.value)}
                >
                  {option.label}
                </Button>
              ))}
            </fieldset>
          </div>

          {sections.length === 0 ? (
            <div className="rounded-[16px] border border-borderLight bg-white p-10 text-center">
              <Typography size="sm" color="textSecondary">
                {items.length === 0 ? "대응할 문의가 없습니다." : "이 필터에 해당하는 문의가 없습니다."}
              </Typography>
            </div>
          ) : (
            sections.map((section) => (
              <section key={section.group} className="flex flex-col gap-4" aria-label={section.title}>
                <Typography asChild size="lg" weight={600} color="textPrimary">
                  <h2>
                    {section.title} <span className="text-textMuted font-normal">({section.items.length})</span>
                  </h2>
                </Typography>
                {section.items.map((item) => (
                  <FollowupCard key={item.ticket.id} item={item} />
                ))}
              </section>
            ))
          )}
        </>
      ) : null}
    </div>
  );
}

function FollowupCard({ item }: { item: SupportFollowupItem }) {
  const { ticket } = item;
  const done = isFollowupDone(item);
  const closed = ticket.status === "resolved";

  return (
    <article
      className={cn(
        "bg-white rounded-[16px] border p-6 flex flex-col gap-5",
        closed ? "border-purple-300" : done ? "border-colorBlue" : "border-borderLight",
      )}
      data-testid={`followup-${ticket.id}`}
    >
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex flex-col gap-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {item.order != null ? (
              <Typography size="xs" weight={600} color="textMuted">
                #{item.order}
              </Typography>
            ) : null}
            <Link to={`/admin/support/${ticket.id}`} className="underline">
              <Typography size="sm" weight={600} color="textPrimary" asChild>
                <span>{ticket.reference}</span>
              </Typography>
            </Link>
            <TicketStatusChip {...ticketStatusDisplay[ticket.status]} />
          </div>
          <Typography size="md" weight={600} color="textPrimary">
            {ticket.subject}
          </Typography>
          <Typography size="xs" color="textMuted">
            {ticket.name} ({ticket.email}) · 접수 {formatTicketDateTime(ticket.createdAt)}
          </Typography>
        </div>
      </header>

      {item.summary != null && item.summary.trim().length > 0 ? (
        <div className="rounded-[12px] bg-backgroundLight p-4">
          <Typography size="2xs" weight={600} color="textMuted" className="mb-1 block">
            요약 / 해야 할 일
          </Typography>
          <Typography size="sm" color="textPrimary" className="whitespace-pre-wrap break-words leading-[1.6]">
            {item.summary}
          </Typography>
        </div>
      ) : null}

      <FollowupChecklist item={item} />

      <div className="flex flex-col gap-3">
        <Typography size="xs" weight={600} color="textSecondary">
          대화 전체 ({item.messages.length})
        </Typography>
        {item.messages.length === 0 ? (
          <Typography size="sm" color="textMuted">
            메시지가 없습니다.
          </Typography>
        ) : (
          item.messages.map((message) => <FollowupMessage key={message.id} message={message} />)
        )}
      </div>

      {closed ? null : <MessageComposer ticketId={ticket.id} customerEmail={ticket.email} />}
    </article>
  );
}

export function FollowupChecklist({ item }: { item: SupportFollowupItem }) {
  const { mutate: setResolved, isPending: isToggling } = useAdminSupportFollowupMutation();
  const { mutate: updateStatus, isPending: isClosing } = useAdminSupportTicketStatusMutation(item.ticket.id);
  const { info, warning } = useToastMessage();
  const actionDone = item.resolvedAt != null;
  const closed = item.ticket.status === "resolved";
  const checkboxId = `followup-resolved-${item.ticket.id}`;

  const handleToggle = (resolved: boolean) =>
    setResolved(
      { ticketId: item.ticket.id, resolved },
      {
        onError: (mutationError) =>
          warning(KnownError.is(mutationError) ? mutationError.message : "조치 완료 상태를 저장하지 못했습니다."),
      },
    );

  const handleClose = () =>
    updateStatus(
      { status: "resolved" },
      {
        onSuccess: () => info("문의를 종결했습니다."),
        onError: (mutationError) =>
          warning(KnownError.is(mutationError) ? mutationError.message : "문의를 종결하지 못했습니다."),
      },
    );

  return (
    <div className="rounded-[12px] border border-borderLight p-4 flex flex-col gap-3">
      <div className="flex items-center gap-3 flex-wrap">
        <input
          id={checkboxId}
          type="checkbox"
          className="size-4 accent-blue-600 cursor-pointer disabled:cursor-default"
          checked={actionDone}
          disabled={isToggling}
          onChange={(event) => handleToggle(event.target.checked)}
        />
        <label htmlFor={checkboxId} className="cursor-pointer">
          <Typography size="sm" weight={600} color="textPrimary" asChild>
            <span>조치 완료</span>
          </Typography>
        </label>
        <Typography size="xs" className={actionDone ? "text-colorBlue" : "text-colorOrange"}>
          {actionDone
            ? `${item.resolvedBy ?? "알 수 없음"} · ${formatTicketDateTime(item.resolvedAt ?? "")}`
            : "내부 조치 전"}
        </Typography>
      </div>

      <div className="flex items-center gap-3 flex-wrap" data-testid="followup-replied">
        <i
          className={cn(
            "size-4 inline-block flex-shrink-0",
            item.replied ? "i-bp-checked text-colorBlue" : "i-bp-pending text-colorOrange",
          )}
        />
        <Typography size="sm" weight={600} color="textPrimary">
          회신 완료
        </Typography>
        <Typography size="xs" className={item.replied ? "text-colorBlue" : "text-colorOrange"}>
          {item.replied && item.lastReplyAt != null
            ? `마지막 회신 발송 ${formatTicketDateTime(item.lastReplyAt)}`
            : "고객에게 발송된 회신 없음 (회신을 보내면 자동으로 표시됩니다)"}
        </Typography>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Typography size="2xs" color="textMuted">
          {closed ? "종결된 문의입니다." : "조치 완료와 회신 완료가 모두 끝나야 종결할 수 있습니다."}
        </Typography>
        {closed ? (
          <span className="inline-flex items-center gap-1.5 rounded-[10px] px-2 py-1 bg-purple-50 text-purple-600">
            <i className="i-bp-checked size-3.5 inline-block" />
            <Typography size="xs" weight={500} className="text-purple-600" asChild>
              <span>종결됨</span>
            </Typography>
          </span>
        ) : (
          <Button size="sm" onClick={handleClose} disabled={!canCloseFollowup(item) || isClosing} loading={isClosing}>
            문의 종결
          </Button>
        )}
      </div>
    </div>
  );
}

const messageKindLabel: Record<MessageKind, string> = {
  customer: "고객",
  reply: "스태프 회신",
  note: "내부 메모 (고객에게 발송 안 됨)",
};

export function FollowupMessage({ message }: { message: SupportMessage }) {
  const segments = splitQuotedBlocks(message.body);

  return (
    <div
      className={cn(
        "rounded-[12px] p-4 border flex flex-col gap-2",
        message.kind === "note"
          ? "border-dashed border-colorOrange/40 bg-colorOrangeLight/40"
          : message.kind === "reply"
            ? "border-colorBlueLight bg-colorBlueLight/40 ml-6"
            : "border-borderLight bg-white mr-6",
      )}
      data-kind={message.kind}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Typography
            size="2xs"
            weight={600}
            className={cn(
              "tracking-wide",
              message.kind === "note"
                ? "text-colorOrange"
                : message.kind === "reply"
                  ? "text-colorBlue"
                  : "text-textMuted",
            )}
          >
            {messageKindLabel[message.kind]}
          </Typography>
          <Typography size="xs" weight={500} color="textPrimary">
            {message.authorName}
          </Typography>
          <Typography size="2xs" color="textMuted">
            {formatTicketDateTime(message.createdAt)}
          </Typography>
        </div>
        {message.deliveryStatus !== "not_applicable" ? (
          <DeliveryStatusChip {...deliveryStatusDisplay[message.deliveryStatus]} />
        ) : null}
      </div>

      {/* Untrusted customer text: rendered as plain text only, whitespace preserved. */}
      <div className="text-sm text-textPrimary whitespace-pre-wrap break-words leading-[1.6]">
        {segments.map((segment, index) =>
          segment.type === "quote" ? (
            <QuotedBlock key={index} text={segment.text} lineCount={segment.lineCount} />
          ) : (
            <span key={index} className="block">
              {segment.text}
            </span>
          ),
        )}
      </div>

      {message.attachments != null && message.attachments.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          {message.attachments.map((attachment) => (
            <AttachmentRow key={attachment.id} attachment={attachment} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function QuotedBlock({ text, lineCount }: { text: string; lineCount: number }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="block border-l-2 border-borderLight pl-3 my-1 text-textSecondary">
      <button type="button" className="text-xs text-colorBlue underline" onClick={() => setOpen((value) => !value)}>
        {open ? `인용문 접기 (${lineCount}줄)` : `인용문 ${lineCount}줄 펼치기`}
      </button>
      {open ? <span className="block">{text}</span> : null}
    </span>
  );
}
